import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { Form } from "../../../forms/Form";
import { FormInput } from "../../../forms/FormInput";
import { FormSubmit } from "../../../forms/FormSubmit";
import { IVoucher } from "../../../models/catalogue";
import { focusElement } from "../../../utils/keyboardFocus";

import styles from "./index.module.scss";

interface ErrorState {
    non_field_errors?: string[];
    vouchercode?: string[];
    reason?: string;
}

const isDuplicateVoucherCode = (
    codeToCheck: string,
    vouchers: readonly IVoucher[],
): boolean => {
    return vouchers.some(
        (voucher) => voucher.code.toLowerCase() === codeToCheck.toLowerCase(),
    );
};

const convertErrorMessage = (errorMsg: string): string => {
    switch (errorMsg) {
        case "Voucher code unknown":
            return t`This is not a valid promo code`;
        case "This coupon has already been used":
            return t`This promo code has already been used`;
        case "This promo code has already been applied":
            return t`This promo code has already been applied`;
    }
    return errorMsg;
};

const buildErrors = (errors: ErrorState): string[] => {
    let allErrors = errors.non_field_errors || [];
    if (errors.vouchercode) {
        allErrors = allErrors.concat(errors.vouchercode);
    }
    if (errors.reason) {
        allErrors = allErrors.concat(errors.reason);
    }
    return allErrors.map((errorMsg) => {
        return convertErrorMessage(errorMsg);
    });
};

export const BasketVoucherForm = (
    props: Immutable<{
        addVoucherCode: (code: string) => Promise<void>;
        vouchers: readonly IVoucher[];
    }>,
): React.ReactNode => {
    const [isSaving, setIsSaving] = React.useState(false);
    const [vouchercode, setVouchercode] = React.useState("");
    const [promocodeFormExpanded, setPromocodeFormExpanded] =
        React.useState(false);
    const [errors, setErrors] = React.useState<ErrorState>({});
    const formField = React.useRef<HTMLInputElement>(null);
    const hadErrorsRef = React.useRef(false);

    // Focus management when form expands or errors occur
    React.useEffect(() => {
        const elem = formField.current;
        if (promocodeFormExpanded && elem) {
            focusElement(elem);
        }
    }, [promocodeFormExpanded]);

    // Focus on input when errors transition from empty -> non-empty
    React.useEffect(() => {
        const elem = formField.current;
        const hasErrors = Object.keys(errors).length > 0;

        if (!hadErrorsRef.current && hasErrors && elem) {
            focusElement(elem);
        }

        hadErrorsRef.current = hasErrors;
    }, [errors]);

    const onChange = (event: React.FormEvent<HTMLInputElement>): void => {
        setVouchercode(event.currentTarget.value);
    };

    const onSubmit = async (
        event: React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        event.preventDefault();
        setIsSaving(true);
        setErrors({});

        // Client-side validation: Check for duplicate voucher code
        if (isDuplicateVoucherCode(vouchercode, props.vouchers)) {
            setErrors({
                vouchercode: ["This promo code has already been applied"],
            });
            setIsSaving(false);
            return; // Stop execution, don't make API call
        }

        // Existing server-side validation via API call
        try {
            await props.addVoucherCode(vouchercode);
            setVouchercode("");
        } catch (err: unknown) {
            // Error is the response body from rejectWithValue in the thunk
            setErrors(err as ErrorState);
        }
        setIsSaving(false);
    };

    const onToggleFormExpanded = (): void => {
        setPromocodeFormExpanded(!promocodeFormExpanded);
    };

    const expandIndicator = promocodeFormExpanded ? "-" : "+";
    return (
        <>
            <button
                type="button"
                aria-expanded={promocodeFormExpanded}
                aria-controls="basket-summary-promocode-form"
                className={styles.trigger}
                onClick={onToggleFormExpanded}
            >
                {t`Enter a Promo Code ${expandIndicator}`}
            </button>
            {promocodeFormExpanded && (
                <div
                    id="basket-summary-promocode-form"
                    // Should be standardized across sites
                    // and refactored into CSS Modules
                    className={styles.wrapper}
                >
                    <Form onSubmit={onSubmit} className={styles.form}>
                        <FormInput
                            inputRef={formField}
                            type="text"
                            id="checkout-promo-code"
                            name="checkout-promo-code"
                            className={styles.input}
                            // TODO(i18n): Use t tagged template: label={t`Enter a Promo Code`}
                            label="Enter a Promo Code"
                            labelPlacement="outside"
                            labelCSSClass={styles.label}
                            placeholder=""
                            required={true}
                            value={vouchercode}
                            errors={buildErrors(errors)}
                            onChange={onChange}
                            disabled={isSaving}
                        />
                        <FormSubmit
                            // TODO(i18n): Use t tagged template: value={t`Apply`}
                            value="Apply"
                            disabled={isSaving}
                            className={styles.submitButton}
                        />
                    </Form>
                </div>
            )}
        </>
    );
};
